import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { certificateService } from '../services/certificateService'
import { Certificate, CertificateType, CertificateFormData } from '../types/certificate'
import { CertificateForm } from '../components/CertificateForm'
import { CertificateList } from '../components/CertificateList'

export function SettingsCertificates() {
  const queryClient = useQueryClient()
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingCertificate, setEditingCertificate] = useState<Certificate | null>(null)
  const [filterType, setFilterType] = useState<CertificateType | undefined>()
  const [pcrsUsername, setPcrsUsername] = useState('')
  const [pcrsPassword, setPcrsPassword] = useState('')

  const getSaveErrorMessage = () => {
    const error = createMutation.error || updateMutation.error
    const message = (error as any)?.response?.data?.message || (error as Error | undefined)?.message
    return message || 'Failed to save certificate. Please try again.'
  }

  const { data: certificates, isLoading, error } = useQuery({
    queryKey: ['certificates'],
    queryFn: () => certificateService.getAll(),
  })

  const createMutation = useMutation({
    mutationFn: (data: CertificateFormData) => certificateService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
      setShowAddForm(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CertificateFormData> }) =>
      certificateService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
      setEditingCertificate(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => certificateService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
    },
  })

  const activateMutation = useMutation({
    mutationFn: (id: string) => certificateService.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => certificateService.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
    },
  })

  const validateMutation = useMutation({
    mutationFn: (id: string) => certificateService.validate(id),
  })

  const { data: pcrsCredentialsStatus } = useQuery({
    queryKey: ['certificates', 'pcrs-credentials'],
    queryFn: () => certificateService.getPcrsCredentialsStatus(),
  })

  const pcrsCredentialsMutation = useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      certificateService.updatePcrsCredentials(username, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificates', 'pcrs-credentials'] })
      setPcrsUsername('')
      setPcrsPassword('')
    },
  })

  const handleSubmit = async (data: CertificateFormData) => {
    if (editingCertificate) {
      await updateMutation.mutateAsync({ id: editingCertificate.id, data })
    } else {
      await createMutation.mutateAsync(data)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      try {
        await deleteMutation.mutateAsync(id)
      } catch (error) {
        alert('Failed to delete certificate')
      }
    }
  }

  const handleActivate = async (id: string) => {
    try {
      await activateMutation.mutateAsync(id)
    } catch (error) {
      alert('Failed to activate certificate')
    }
  }

  const handleRevoke = async (id: string) => {
    if (window.confirm('Are you sure you want to revoke this certificate?')) {
      try {
        await revokeMutation.mutateAsync(id)
      } catch (error) {
        alert('Failed to revoke certificate')
      }
    }
  }

  const handleValidate = async (id: string) => {
    try {
      const result = await validateMutation.mutateAsync(id)
      if (result.valid) {
        alert('Certificate is valid')
      } else {
        alert(`Certificate validation failed: ${result.message || 'Unknown error'}`)
      }
    } catch (error) {
      alert('Failed to validate certificate')
    }
  }

  if (showAddForm || editingCertificate) {
    return (
      <div className="settings-content" role="main">
        <div className="settings-header">
          <h1>
            {editingCertificate ? 'Edit Certificate' : 'Add New Certificate'}
          </h1>
          <button
            className="btn-secondary"
            onClick={() => {
              setShowAddForm(false)
              setEditingCertificate(null)
            }}
          >
            Back to Certificates
          </button>
        </div>
        {(createMutation.isError || updateMutation.isError) && (
          <div className="error-message" role="alert">
            {getSaveErrorMessage()}
          </div>
        )}
        <CertificateForm
          initialData={editingCertificate ? undefined : undefined}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowAddForm(false)
            setEditingCertificate(null)
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </div>
    )
  }

  return (
    <div className="settings-content" role="main">
      <div className="settings-header">
        <div>
          <h1>Certificates</h1>
          <p className="settings-subtitle">Manage security certificates for PCRS and Healthlink</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowAddForm(true)}
          aria-label="Add new certificate"
        >
          + Add Certificate
        </button>
      </div>

      <div className="settings-filters">
        <div className="filter-group">
          <label htmlFor="certificateTypeFilter">Filter by Type</label>
          <select
            id="certificateTypeFilter"
            value={filterType || ''}
            onChange={(e) => setFilterType(e.target.value as CertificateType || undefined)}
            aria-label="Filter certificates by type"
          >
            <option value="">All Certificates</option>
            <option value="PCRS">PCRS</option>
            <option value="Healthlink">Healthlink</option>
          </select>
        </div>
      </div>

      {isLoading && (
        <div className="loading" role="status" aria-live="polite">
          Loading certificates...
        </div>
      )}

      {error && (
        <div className="error" role="alert" aria-live="assertive">
          Error loading certificates
        </div>
      )}

      {!isLoading && !error && certificates && (
        <CertificateList
          certificates={certificates}
          onEdit={setEditingCertificate}
          onDelete={handleDelete}
          onActivate={handleActivate}
          onRevoke={handleRevoke}
          onValidate={handleValidate}
          filterType={filterType}
        />
      )}

      <div className="settings-info">
        <h2>PCRS Login Credentials</h2>
        <p className="settings-subtitle">
          Stored in Secrets Manager (same approach as certificate passwords). Used by the PCRS broker
          for patient lookup and claim submission.
        </p>
        {pcrsCredentialsStatus?.hasCredentials && (
          <p className="field-hint" style={{ marginBottom: '1rem' }}>
            ✓ Credentials configured
          </p>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (pcrsUsername.trim() && pcrsPassword) {
              pcrsCredentialsMutation.mutate({ username: pcrsUsername.trim(), password: pcrsPassword })
            }
          }}
          style={{ maxWidth: '400px', marginBottom: '2rem' }}
        >
          <div className="form-group">
            <label htmlFor="pcrs-username">Username</label>
            <input
              id="pcrs-username"
              type="text"
              value={pcrsUsername}
              onChange={(e) => setPcrsUsername(e.target.value)}
              autoComplete="username"
              placeholder="PCRS login username"
            />
          </div>
          <div className="form-group">
            <label htmlFor="pcrs-password">Password</label>
            <input
              id="pcrs-password"
              type="password"
              value={pcrsPassword}
              onChange={(e) => setPcrsPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            className="btn-primary"
            disabled={!pcrsUsername.trim() || !pcrsPassword || pcrsCredentialsMutation.isPending}
          >
            {pcrsCredentialsMutation.isPending ? 'Saving…' : 'Save PCRS Credentials'}
          </button>
          {pcrsCredentialsMutation.isError && (
            <p className="error-message" role="alert">
              Failed to save credentials
            </p>
          )}
        </form>
      </div>

      <div className="settings-info">
        <h2>Certificate Management</h2>
        <div className="info-sections">
          <div className="info-section">
            <h3>PCRS Certificates</h3>
            <p>
              Primary Care Reimbursement Service certificates are used for secure communication
              with the HSE PCRS system. These certificates must be valid and not expired.
            </p>
            <ul>
              <li>Certificates are typically issued by the HSE</li>
              <li>Format: .p12 or .pfx files</li>
              <li>Must be renewed before expiration</li>
            </ul>
          </div>
          <div className="info-section">
            <h3>Healthlink Certificates</h3>
            <p>
              Healthlink certificates enable secure messaging between healthcare providers.
              These certificates require a password for access.
            </p>
            <ul>
              <li>Certificates are issued by Healthlink</li>
              <li>Format: .p12 or .pfx files with password</li>
              <li>Used for secure message transmission</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}



































